import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import cn from 'classnames';

import { PO_BOX_RE } from 'common/regex';
import { setHeaderFooterVisibility } from 'actions/headerfooter';
import {
  fetchShippingInfo,
  getNewAddressId,
  updateMailingAddressOrName,
  updateShippingPhone
} from 'actions/account/shipping';
import { logoutAsCustomer } from 'actions/account/account';
import {
  fetchPin,
  getTrackingNumber,
  validateUserPin
} from 'actions/cart';
import successImg from 'images/success.svg';
import truckImg from 'images/icon-truck.svg';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import { ButtonSpinner, PageLoader } from 'components/Loader';
import MultiLineAddress from 'components/checkout/melody/address/MultiLineAddress';
import ShippingForm from 'components/account/ShippingForm';
import css from 'styles/containers/createLabel.scss';

export class CreateLabel extends Component {
  static beforeFetchDataOnServer({ dispatch }) {
    return dispatch(setHeaderFooterVisibility(false));
  }

  state = {
    step: 'SELECT_ADDRESS',
    validPin: false
  };

  componentDidMount() {
    const { shipping: { addresses }, fetchShippingInfo, setHeaderFooterVisibility } = this.props;

    setHeaderFooterVisibility(false);

    if (!addresses) {
      fetchShippingInfo(null, '/create-label');
    }
  }

  componentWillUnmount() {
    this.props.setHeaderFooterVisibility(true);
  }

  makeLogo = () => {
    const { content } = this.props;
    const data = content?.Header?.slotData?.logo?.images?.[0];

    return (data &&
      <img
        src={data.src}
        alt={data.alt}
        height={data.height}
        width={data.width}/>
    );
  }

  onAddressSelected = ({ currentTarget }) => {
    const { dataset: { addressid } } = currentTarget;

    if (addressid) {
      this.setState({ selectedAddressId: addressid });
    }
  }

  onAddAddress = e => {
    e.preventDefault();
    this.setState({ step: 'ADD_ADDRESS' });
  }

  onSelectAddress = () => {
    this.setState({ step: 'SELECT_ADDRESS' });
  }

  onSaveAddress = () => {
    const { shipping: { address }, getNewAddressId, fetchShippingInfo } = this.props;
    getNewAddressId(address, resp => {
      const { addressId } = resp;
      fetchShippingInfo(null, '/create-label');

      if (addressId) {
        this.setState({ step: 'SELECT_ADDRESS', selectedAddressId: addressId });
      }
    }, { forceOriginal: true, saveInvalidAddress: false });
  }

  onPinPoll = () => {
    const { validateUserPin, cart: { pin, isValid } } = this.props;

    if (!isValid) {
      setTimeout(this.onPinPoll, 1000);
      pin && validateUserPin(pin);
    } else {
      this.onDone();
    }
  }

  onShowPin = () => {
    this.setState({ step: 'SHOW_PIN' });
    this.props.fetchPin();
    this.onPinPoll();
  }

  onDone = () => {
    const {
      getTrackingNumber,
      logoutAsCustomer,
      cart: { pin }
    } = this.props;

    const { selectedAddressId } = this.state;

    getTrackingNumber({ pin, addressId: selectedAddressId }, () => {
      this.setState({ step: 'ALL_DONE' });
      logoutAsCustomer();
      setTimeout(this.onStartOver, 60000); // send customers back to the beginning after one minute
    });
  }

  onStartOver = () => {
    const { logoutAsCustomer, fetchShippingInfo } = this.props;
    logoutAsCustomer().then(() => {
      fetchShippingInfo(null, '/create-label');
    });
  }

  checkInvalidAddress = address => {
    const { mailingAddress: { addressLine1, addressLine2, stateOrRegion } } = address;
    return addressLine1.match(PO_BOX_RE)
    || addressLine2?.match(PO_BOX_RE)
    || stateOrRegion === 'HI'
    || stateOrRegion === 'AK';
  }

  render() {
    const {
      cart: { pin, trackingNumber },
      shipping,
      updateMailingAddressOrName,
      updateShippingPhone
    } = this.props;

    const { addresses, isLoading } = shipping;

    const {
      step,
      selectedAddressId
    } = this.state;

    if (!addresses || isLoading) {
      return <PageLoader centered={true}/>;
    }

    return (
      <SiteAwareMetadata>
        <div className={css.container}>
          <div className={css.header}>
            { this.makeLogo() }
            <h2>Delivery Service</h2>
          </div>

          {/* select address */}
          {
            (!!addresses.length && step === 'SELECT_ADDRESS') &&
              <>
                <h1>Select Shipping Address</h1>
                  {
                    addresses.map(address => {
                      const { addressId } = address;
                      const isInvalidAddress = this.checkInvalidAddress(address);
                      const isSelected = selectedAddressId && (selectedAddressId === addressId);

                      return (
                        <Fragment key={addressId}>
                          <input
                            defaultChecked={isSelected}
                            disabled={isInvalidAddress}
                            id={`address-${addressId}`}
                            name="address"
                            type="radio" />
                          <label
                            onClick={this.onAddressSelected}
                            data-addressid={isInvalidAddress ? null : addressId}
                            className={cn(css.item, { [css.selected]: isSelected, [css.disabled]: isInvalidAddress })}
                            htmlFor={`address-${addressId}`}>
                            <MultiLineAddress
                              address={address}
                              hideCountry={true}
                              hidePhone={true} />
                          </label>
                          { isInvalidAddress && <p>Cannot deliver to this address</p> }
                        </Fragment>
                      );
                    })
                  }
                <div className={css.ctaContainer}>
                  <button
                    type="button"
                    className={css.primary}
                    onClick={this.onShowPin}
                    disabled={!selectedAddressId}>Next</button>
                  <Link to={'/addresses/new'} className={css.add} onClick={this.onAddAddress}>Add an address</Link>
                </div>
              </>
          }

          {/* add new address */}
          {
            (!addresses.length || step === 'ADD_ADDRESS') &&
              <>
                <h1>Add an Address</h1>
                <p className={css.note}>Note: Zappos cannot deliver to P.O. Boxes or international addresses</p>
                <ShippingForm
                  isReducedInfo={true}
                  primaryVerbiage="Use Address"
                  shipping={shipping}
                  onChangeShippingAddress={this.onSaveAddress}
                  onUpdateMailingAddressOrName={updateMailingAddressOrName}
                  onUpdateShippingPhone={updateShippingPhone}
                  onCancelClick={this.onSelectAddress}
                  onResetShippingForm={f => f}/>
              </>
          }

          {/* show pin */}
          {
            step === 'SHOW_PIN' &&
              <>
                <h1>Show this shipping code to the sales agent</h1>
                <p className={css.pin}>{pin}</p>
                <div className={css.waiting}>
                  <ButtonSpinner className={css.spinner} /> Waiting for agent to enter code
                </div>
              </>
          }

          {/* all done */}
          {
            step === 'ALL_DONE' &&
              <>
                <div className={css.success}><img src={successImg} alt="Success!" /></div>
                <h1>All Done! Here's your tracking info</h1>
                <p className={css.note}>It may take up to 24 hours to return information.</p>
                <div className={css.tracking}>
                  <img src={truckImg} alt="" />
                  <a
                    className={css.tracking}
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`https://www.ups.com/track?loc=en_US&tracknum=${trackingNumber}`}>Tracking: {trackingNumber}</a>
                </div>
                <button type="button" className={css.finished} onClick={this.onStartOver}>Finished</button>
              </>
          }

          { step !== 'ALL_DONE' && <button type="button" className={css.startOver} onClick={this.onStartOver}>Start Over</button> }
        </div>
      </SiteAwareMetadata>
    );
  }
}

export const mapStateToProps = state => ({
  cart: state.cart,
  shipping: state.shipping,
  content: state.headerFooter.content
});

const mapDispatchToProps = {
  fetchPin,
  fetchShippingInfo,
  getTrackingNumber,
  getNewAddressId,
  logoutAsCustomer,
  setHeaderFooterVisibility,
  updateMailingAddressOrName,
  updateShippingPhone,
  validateUserPin
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateLabel);
